import { useMemo } from 'react';
import { useApi } from './apiService';

// Types mirror Defatom.Cloud.AuditAPI's response models (AuditEventResponse / AuditLogPageResponse),
// which follow the event contract in Defatom.Shared docs/audit-trail-design.md §5. Enums are serialized as strings.

export type AuditCategory = 'Change' | 'Security';
export type AuditOutcome = 'Succeeded' | 'Denied' | 'Failed';
export type AuditActorType = 'User' | 'System';
export type AuditStore = 'Sql' | 'Cosmos';
export type AuditChangeOperation = 'Created' | 'Updated' | 'Deleted' | 'Upserted';

export const AUDIT_CATEGORIES: AuditCategory[] = ['Change', 'Security'];
export const AUDIT_OUTCOMES: AuditOutcome[] = ['Succeeded', 'Denied', 'Failed'];

export interface AuditActor {
  type: AuditActorType;
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  /** The token's tenant_id. Never read alone — use AuditEvent.actorHomeTenantId (design §6). */
  tenantId?: string | null;
  /** The token's home_tenant_id (null today). Never read alone — use AuditEvent.actorHomeTenantId (design §6). */
  homeTenantId?: string | null;
  clientId?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  jobName?: string | null;
}

export interface AuditHttpInfo {
  method: string;
  route?: string | null;
  statusCode: number;
}

export interface AuditFieldChange {
  /** SQL property name, or JSON pointer path for Cosmos (e.g. /policyContent/enabled). */
  name: string;
  /** Any JSON value, already redacted upstream ("***" masked, "sha256:…" hashed, "…[truncated]"). */
  old?: unknown;
  new?: unknown;
  /** For an id field (TenantAgentID, RemediationPlanID, TenantID...): the current name of the record `old`/`new` point to. */
  oldName?: string | null;
  newName?: string | null;
}

export interface AuditChange {
  store: AuditStore;
  /** SQL entity (EF class name) or Cosmos container key, e.g. RemediationPlan; shown via auditEntities.ts labels. */
  entity: string;
  key: string;
  /** The record's own name (plan, group, device…) when the API found one in its recorded fields; sent to every role. */
  displayName?: string | null;
  tenantId?: string | null;
  operation: AuditChangeOperation;
  /** Always empty when fieldsOmitted is true. */
  fields: AuditFieldChange[];
  fieldsOmitted: boolean;
}

// Globaladmin gets whole events. Every other role gets a summary in this same shape: correlationId, failureReason,
// actorHomeTenantId and the actor's ids/tenant/ip/userAgent are null, and every change's `fields` is empty.
export interface AuditEvent {
  eventId: string;
  schemaVersion?: string | null;
  category: AuditCategory;
  correlationId?: string | null;
  timestampUtc: string;
  service: string;
  action: string;
  outcome: AuditOutcome;
  failureReason?: string | null;
  actor?: AuditActor | null;
  /** Computed by the API: actor.homeTenantId ?? actor.tenantId. The only field to use for the actor's own tenant. */
  actorHomeTenantId?: string | null;
  targetTenantId?: string | null;
  /** Lowercase tenant GUID, or "global". */
  tenantKey: string;
  http?: AuditHttpInfo | null;
  changes: AuditChange[];
  truncated: boolean;
  omittedChangeCount: number;
}

export interface AuditLogPage {
  /** Newest first. */
  items: AuditEvent[];
  /** The effective range after the API's defaults. */
  fromUtc: string;
  toUtc: string;
}

/** ResponseModel.Pagination (Defatom.Core), the same block every paged platform API returns. */
export interface AuditPagination {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface AuditResponse<T> {
  isSuccess: boolean;
  message?: string;
  data?: T;
  /** List endpoints only. With `limit`, page 1 of size `limit`: totalRecords > limit means the result was cut short. */
  pagination?: AuditPagination | null;
}

/** Query-string filters shared by every AuditAPI list endpoint. All optional; `from`/`to` are ISO 8601. */
export interface AuditLogQuery {
  from?: string;
  to?: string;
  actorEmail?: string;
  action?: string;
  entity?: string;
  entityKey?: string;
  category?: AuditCategory;
  outcome?: AuditOutcome;
  /** 1-based; default 1. Ignored with `limit`. */
  pageNumber?: number;
  /** 1-100; default 50. Ignored with `limit`. */
  pageSize?: number;
  /** 1-1000: the newest `limit` matching events in one response (CSV download) instead of a page. */
  limit?: number;
}

// Order matches AuditLogQueryParameters; only non-empty values are sent so the API's defaults apply.
const QUERY_KEYS: (keyof AuditLogQuery)[] = [
  'from',
  'to',
  'actorEmail',
  'action',
  'entity',
  'entityKey',
  'category',
  'outcome',
  'pageNumber',
  'pageSize',
  'limit',
];

export const buildAuditLogQueryString = (query: AuditLogQuery): string => {
  const params = new URLSearchParams();
  QUERY_KEYS.forEach((key) => {
    const value = query[key];
    if (value === undefined || value === null) return;
    const text = String(value).trim();
    if (text) params.append(key, text);
  });
  return params.toString();
};

const withQuery = (path: string, query: AuditLogQuery) => {
  const qs = buildAuditLogQueryString(query);
  return qs ? `${path}?${qs}` : path;
};

export const getTenantAuditEventsEndpoint = (tenantId: string, query: AuditLogQuery) =>
  withQuery(`auditservice/auditlog/tenants/${encodeURIComponent(tenantId)}/events`, query);

// Globaladmin only (enforced by the API).
export const getGlobalAuditEventsEndpoint = (query: AuditLogQuery) => withQuery('auditservice/auditlog/global/events', query);

// Globaladmin only (enforced by the API): every tenant and global, always bounded by the time range.
export const getAllTenantsAuditEventsEndpoint = (query: AuditLogQuery) => withQuery('auditservice/auditlog/events', query);

/** One audit action: the recorded code (sent as the `action` filter) and the customer-facing name (Defatom.Shared AuditActionEnum). */
export interface AuditActionOption {
  action: string;
  displayName: string;
}

// Every role that can open the Activity log. Sorted by displayName by the API.
export const AUDIT_ACTIONS_ENDPOINT = 'auditservice/auditlog/actions';

export const useAuditService = () => {
  const api = useApi();

  // Memoized so callers can list these in effect dependencies (`api` is the shared singleton).
  return useMemo(
    () => ({
      getTenantEvents: async (tenantId: string, query: AuditLogQuery) => {
        const response = await api.get<AuditResponse<AuditLogPage>>(getTenantAuditEventsEndpoint(tenantId, query));
        return response.data;
      },
      getGlobalEvents: async (query: AuditLogQuery) => {
        const response = await api.get<AuditResponse<AuditLogPage>>(getGlobalAuditEventsEndpoint(query));
        return response.data;
      },
      searchAllTenants: async (query: AuditLogQuery) => {
        const response = await api.get<AuditResponse<AuditLogPage>>(getAllTenantsAuditEventsEndpoint(query));
        return response.data;
      },
      getActions: async () => {
        const response = await api.get<AuditResponse<AuditActionOption[]>>(AUDIT_ACTIONS_ENDPOINT);
        return response.data;
      },
    }),
    [api],
  );
};
